import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { schemaService, domainService } from '../../services';
import { ExtractionSchema, Domain } from '../../types';
import { SchemaFormModal } from '../../components/admin/SchemaFormModal';
import { SchemaViewerModal } from '../../components/admin/SchemaViewerModal';
import { FilterableTable } from '../../components/ui';
import type { FilterableColumnDef } from '../../components/ui';
import { PageContainer } from '../../components/layout';
import { UsagesModal } from '../../components/catalog/UsagesModal';
import { useCatalogUsages } from '../../components/catalog/useCatalogUsages';

export const SchemasPage: React.FC = () => {
  const navigate = useNavigate();
  const [schemas, setSchemas] = useState<ExtractionSchema[]>([]);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSchema, setSelectedSchema] = useState<ExtractionSchema | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showViewer, setShowViewer] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [duplicateMode, setDuplicateMode] = useState(false);

  const usages = useCatalogUsages('schemas');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [schemasData, domainsData] = await Promise.all([
        schemaService.list({ include_deprecated: true }),
        domainService.list()
      ]);
      setSchemas(schemasData);
      setDomains(domainsData);
    } catch (error) {
      console.error('Error loading schemas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (schema: ExtractionSchema) => {
    if (!confirm(`Supprimer le schema "${schema.name}" ?`)) return;
    await usages.handleDeleteWithGuard(
      schema.id,
      schema.name,
      () => schemaService.delete(schema.id),
      () => loadData()
    );
  };

  const handleDeprecate = async (schema: ExtractionSchema) => {
    try {
      await schemaService.update(schema.id, { is_deprecated: !schema.is_deprecated });
      loadData();
    } catch (error) {
      console.error('Error updating schema:', error);
    }
  };

  const columns: FilterableColumnDef<ExtractionSchema>[] = useMemo(() => [
    {
      key: 'name',
      header: 'Nom',
      sortable: true,
      searchable: true,
      render: (s) => s.name,
    },
    {
      key: 'version',
      header: 'Version',
      sortable: true,
      render: (s) => s.version,
    },
    {
      key: 'domain',
      header: 'Domaine',
      filterable: true,
      searchable: true,
      render: (s) => s.domain,
    },
    {
      key: 'fields',
      header: 'Champs',
      sortable: true,
      sortValueFn: (s) => String(s.field_count).padStart(6, '0'),
      render: (s) => (
        <span className="text-gray-600 text-sm">{s.field_count} champs</span>
      ),
    },
    {
      key: 'status',
      header: 'Statut',
      filterable: true,
      filterValueFn: (s) => s.is_deprecated ? ['deprecated'] : ['actif'],
      filterOptions: [
        { value: 'actif', label: 'Actif' },
        { value: 'deprecated', label: 'Deprecated' },
      ],
      render: (s) => (
        <div className="flex gap-1">
          {s.is_deprecated && (
            <span className="px-2 py-0.5 bg-orange-100 text-orange-800 rounded text-xs">Deprecated</span>
          )}
        </div>
      ),
    },
    {
      key: 'usages',
      header: 'Activé dans',
      sortable: true,
      sortValueFn: (s) => String(s.tenants_count ?? 0).padStart(6, '0'),
      render: (s) => (
        <button
          onClick={() => usages.openModal(s.id, s.name)}
          className="text-blue-600 hover:underline text-sm"
        >
          {s.tenants_count ?? 0} tenants
        </button>
      ),
    },
    {
      key: 'created_by',
      header: 'Créé par',
      filterable: true,
      filterValueFn: (s) => s.created_by_name ? [s.created_by_name] : [],
      render: (s) => <span className="text-sm text-gray-500">{s.created_by_name || '-'}</span>,
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (s) => (
        <div className="flex gap-2">
          <button onClick={() => { setSelectedSchema(s); setShowViewer(true); }} className="text-blue-600 hover:underline text-sm">
            Voir
          </button>
          <button onClick={() => { setSelectedSchema(s); setEditMode(true); setDuplicateMode(false); setShowForm(true); }} className="text-blue-600 hover:underline text-sm">
            Modifier
          </button>
          <button onClick={() => navigate(`/parametres/schemas/${s.id}/edit`)} className="text-purple-600 hover:underline text-sm">
            Éditer
          </button>
          <button onClick={() => { setSelectedSchema(s); setEditMode(false); setDuplicateMode(true); setShowForm(true); }} className="text-teal-600 hover:underline text-sm">
            Dupliquer
          </button>
          <button onClick={() => handleDeprecate(s)} className="text-orange-600 hover:underline text-sm">
            {s.is_deprecated ? 'Restaurer' : 'Deprecate'}
          </button>
          <button
            onClick={() => handleDelete(s)}
            className="text-sm text-red-600 hover:underline"
            title="Supprimer"
          >
            Supprimer
          </button>
        </div>
      ),
    },
  ], [navigate, usages]);

  return (
    <PageContainer>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Gestion des Schemas</h1>
        <button
          onClick={() => { setEditMode(false); setDuplicateMode(false); setSelectedSchema(null); setShowForm(true); }}
          className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-600"
        >
          + Nouveau Schema
        </button>
      </div>

      {loading ? (
        <div>Chargement...</div>
      ) : (
        <FilterableTable
          persistKey="parametres-schemas"
          paginated
          data={schemas}
          columns={columns}
          rowKey={(s) => s.id}
          defaultSort={{ key: 'name', direction: 'asc' }}
          rowClassName={(s) => s.is_deprecated ? 'text-gray-400' : ''}
          noDataMessage="Aucun schema trouvé"
          emptyMessage="Aucun schema ne correspond aux filtres"
        />
      )}

      {showForm && (
        <SchemaFormModal
          schema={editMode || duplicateMode ? selectedSchema : null}
          domains={domains}
          isDuplicate={duplicateMode}
          onClose={() => setShowForm(false)}
          onSave={() => { setShowForm(false); loadData(); }}
        />
      )}

      {showViewer && selectedSchema && (
        <SchemaViewerModal
          schema={selectedSchema}
          onClose={() => setShowViewer(false)}
        />
      )}

      <UsagesModal
        open={usages.modal.open}
        catalogType="schemas"
        objectId={usages.modal.objectId}
        objectLabel={usages.modal.objectLabel}
        onClose={usages.closeModal}
      />
    </PageContainer>
  );
};
